import { InsuredFilesProvider } from "@qw/insured";
import {
  Column,
  ColumnContent,
  ColumnHeader,
  Icon,
  PanelOutlet,
  type PanelOutletProps,
  SetMetadata,
  TabsNav,
  TabsNavLink,
} from "@qw/ui";
import { type FC, useEffect, useState } from "react";
import { useParams } from "react-router";
import { INBOX_OUTLET_HOST, useInboxRail } from "@/inbox/use-inbox-rail";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { useConversation } from "@/providers/use-conversation";
import { useInboxInsured } from "@/providers/use-inbox-insured";
import { usePage } from "../providers/use-page";
import { LinkInsuredButton } from "./components/link-insured-button";
import { InsuredAgentCard } from "./insured-agent-card";
import { MiniAppActions } from "./mini-app-actions";
import { MiniAppHeader } from "./mini-app-header";

/**
 * The rail's height, for the footer below it to size itself against. `null`
 * while unmeasured, which the footer reads as "not short" — the split-mode
 * mini-app is a full-height panel, so unmeasured and tall want the same thing.
 */
const useRailHeight = () => {
  const [rail, setRail] = useState<HTMLDivElement | null>(null);
  const [railHeight, setRailHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!rail || typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      setRailHeight(entry?.contentRect.height ?? null);
    });

    resizeObserver.observe(rail);

    return () => {
      resizeObserver.disconnect();
    };
  }, [rail]);

  return { railHeight, railRef: setRail };
};

export const MiniApp: FC<PanelOutletProps> = ({ components, fallback }) => {
  const { rootPath } = usePage();
  const { outletHost } = useInboxRail();
  const { conversationId } = useInboxQueryStates();
  const { inboxConversation, loading: conversationLoading } = useConversation();
  const { insured, insuredId, loading: insuredLoading } = useInboxInsured();
  const { insuredId: routeInsuredIdParam } = useParams();
  const { railHeight, railRef } = useRailHeight();

  const loading = conversationLoading || insuredLoading;
  const selectedConversation = conversationId
    ? inboxConversation?.id === conversationId
      ? inboxConversation
      : undefined
    : inboxConversation;
  const routeInsuredId = routeInsuredIdParam === "files" ? undefined : routeInsuredIdParam;
  const tabInsuredId = insuredId ?? (conversationLoading ? routeInsuredId : undefined);
  const miniAppRootPath = tabInsuredId ? `${rootPath}/${tabInsuredId}` : rootPath;

  // The same value that decides where the outlet renders decides whether it has
  // anything to show, so the merged rail can never end up holding a blank panel
  // because the insured toggle it replaced was left off.
  if (outletHost === INBOX_OUTLET_HOST.hidden) {
    return null;
  }

  if (!loading && !insured) {
    return (
      <Column className="bg-background flex-1">
        <ColumnHeader icon={<Icon icon={selectedConversation ? "link_off" : "database_off"} />}>
          <h4 className="flex-auto font-medium text-base truncate">No Insured</h4>
        </ColumnHeader>
        <ColumnContent className="p-2">
          {selectedConversation && <LinkInsuredButton conversationId={selectedConversation.id} />}
        </ColumnContent>
      </Column>
    );
  }

  return (
    <>
      {insured && <SetMetadata key={insured.id} title={insured.name} />}
      <Column className="bg-background flex-1" ref={railRef}>
        <ColumnHeader>
          <MiniAppHeader insured={insured} loading={loading} />
        </ColumnHeader>
        {/* Tabs and actions share a row, so it renders when either has something to
            show. Neither does during a cold load with nothing selected yet, and an
            empty `ColumnHeader` is a bordered 32px of dead space. */}
        {(tabInsuredId || selectedConversation) && (
          <ColumnHeader>
            {tabInsuredId && (
              // Shrinks before the actions do, so the tab labels ellipsize at the
              // 220px drag floor rather than pushing the group off the row.
              <TabsNav className="min-w-0 overflow-hidden">
                <TabsNavLink
                  className="min-w-0 truncate"
                  to={{
                    pathname: miniAppRootPath,
                    search: location.search,
                  }}
                  replace
                  end
                >
                  Active
                </TabsNavLink>
                <TabsNavLink
                  className="min-w-0 truncate"
                  to={{
                    pathname: `${miniAppRootPath}/files`,
                    search: location.search,
                  }}
                  replace
                >
                  Files
                </TabsNavLink>
              </TabsNav>
            )}
            <MiniAppActions
              className="ml-auto"
              inboxConversation={selectedConversation}
              insured={insured}
              insuredId={insuredId}
              rootPath={rootPath}
            />
          </ColumnHeader>
        )}
        {insured ? (
          <InsuredFilesProvider id={insuredId}>
            <PanelOutlet components={components} fallback={fallback} />
          </InsuredFilesProvider>
        ) : (
          <ColumnContent />
        )}
        <InsuredAgentCard agent={insured?.agent} insuredId={insuredId} railHeight={railHeight} />
      </Column>
    </>
  );
};
